import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, User, Send, Loader2, AlertCircle, Settings, GitBranch,
  Timer, MessageSquare, PauseCircle, PlayCircle, CheckCircle, XCircle,
  CircleDot, XOctagon, RefreshCw, FileText, UserCheck, Wrench, Lock,
  ClipboardCheck, GitMerge, Package, Calendar, Edit, Save, ClipboardList, Paperclip, Trash2
} from 'lucide-react';import { incidentAPI, serviceRequestAPI, changeRequestAPI, userAPI, uploadAttachment } from '../services/api';
import { formatDateTime, formatDuration, getTzLabel } from '../utils/timezone';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// ─── Stage definitions ────────────────────────────────────────────────────────
const INC_STAGES = [
  { key: 'submitted',   statuses: ['new', 'categorized'],          label: 'Submitted',   icon: FileText    },
  { key: 'assigned',    statuses: ['assigned'],                    label: 'Assigned',    icon: UserCheck   },
  { key: 'in_progress', statuses: ['in_progress', 'pending_user'], label: 'In Progress', icon: Wrench      },
  { key: 'resolved',    statuses: ['resolved'],                    label: 'Resolved',    icon: CheckCircle },
  { key: 'closed',      statuses: ['closed', 'cancelled', 'reopened'], label: 'Closed',  icon: Lock        },
];
const SR_STAGES = [
  { key: 'submitted',   statuses: ['new'],                         label: 'Submitted',   icon: FileText    },
  { key: 'approval',    statuses: ['pending_approval'],            label: 'Approval',    icon: ClipboardCheck },
  { key: 'assigned',    statuses: ['assigned'],                    label: 'Assigned',    icon: UserCheck   },
  { key: 'in_progress', statuses: ['in_progress', 'pending_user'], label: 'In Progress', icon: Wrench      },
  { key: 'fulfilled',   statuses: ['resolved'],                    label: 'Fulfilled',   icon: Package     },
  { key: 'closed',      statuses: ['closed', 'cancelled', 'reopened'], label: 'Closed',  icon: Lock        },
];
const CR_STAGES = [
  { key: 'submitted',   statuses: ['new'],                         label: 'Submitted',   icon: FileText    },
  { key: 'approval',    statuses: ['pending_approval'],            label: 'Approval',    icon: ClipboardCheck },
  { key: 'assigned',    statuses: ['assigned'],                    label: 'Assigned',    icon: UserCheck   },
  { key: 'in_progress', statuses: ['in_progress', 'pending_user'], label: 'In Progress', icon: Wrench      },
  { key: 'implemented', statuses: ['resolved'],                    label: 'Implemented', icon: GitMerge    },
  { key: 'closed',      statuses: ['closed', 'cancelled', 'reopened'], label: 'Closed',  icon: Lock        },
];

function getStages(type, ticket) {
  if (type === 'incident') return INC_STAGES;
  if (type === 'cr') return CR_STAGES;
  // SR: skip approval stage if no approval required
  if (ticket?.approval_status === 'none') return SR_STAGES.filter(s => s.key !== 'approval');
  return SR_STAGES;
}

function getCurrentStageIndex(stages, status) {
  const idx = stages.findIndex(s => s.statuses.includes(status));
  return idx === -1 ? 0 : idx;
}

function etaLabel(resolution_due) {
  if (!resolution_due) return null;
  const due = new Date(resolution_due);
  const now = new Date();
  const diffMs = due - now;
  const diffH = Math.round(diffMs / 3600000);
  const diffD = Math.round(diffMs / 86400000);
  if (diffMs < 0) return { text: `${Math.abs(diffD) || 1}d overdue`, color: 'text-rose-600 dark:text-rose-400' };
  if (diffH < 1) return { text: 'Due now', color: 'text-amber-600 dark:text-amber-400' };
  if (diffH < 24) return { text: `${diffH}h left`, color: 'text-amber-600 dark:text-amber-400' };
  if (diffD === 1) return { text: 'Tomorrow', color: 'text-amber-500 dark:text-amber-400' };
  return { text: `~${diffD} days`, color: 'text-slate-600 dark:text-slate-300' };
}

// ─── Ticket Status Banner ─────────────────────────────────────────────────────
function TicketStatusBanner({ ticket, type, typeLabel, approvals }) {
  if (!ticket) return null;

  const stages = getStages(type, ticket);
  const currentIdx = getCurrentStageIndex(stages, ticket.status);
  const eta = etaLabel(ticket.resolution_due);

  // Type styling
  const typeStyles = {
    incident: { badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300', dot: '#EF4444' },
    sr:       { badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', dot: '#3B82F6' },
    cr:       { badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', dot: '#8B5CF6' },
  };
  const ts = typeStyles[type] || typeStyles.incident;

  // Priority badge
  const prColors = {
    p1: 'bg-rose-500 text-white', p2: 'bg-amber-500 text-white',
    p3: 'bg-blue-500 text-white', p4: 'bg-slate-400 text-white',
  };
  const urgLabels = { asap: 'ASAP', this_week: 'This Week', standard: 'Standard', no_rush: 'No Rush' };

  // Current status pills
  const statusPills = [];
  if (ticket.status === 'pending_approval') {
    const pendingApproval = approvals?.find(a => a.status === 'pending');
    statusPills.push({ icon: ClipboardCheck, color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700', label: 'Awaiting Approval', sub: pendingApproval?.approver_name || 'approver' });
  } else if (ticket.status === 'assigned') {
    if (ticket.assigned_group_name) statusPills.push({ icon: UserCheck, color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700', label: `Assigned to`, sub: ticket.assigned_group_name });
    if (ticket.assigned_to_name) statusPills.push({ icon: User, color: 'bg-sky-50 dark:bg-sky-900/20 text-sky-800 dark:text-sky-300 border-sky-200 dark:border-sky-700', label: 'with', sub: ticket.assigned_to_name });
  } else if (ticket.status === 'in_progress') {
    if (ticket.assigned_to_name) statusPills.push({ icon: Wrench, color: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700', label: 'In progress by', sub: ticket.assigned_to_name });
    if (ticket.assigned_group_name) statusPills.push({ icon: UserCheck, color: 'bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-700', label: 'Team', sub: ticket.assigned_group_name });
  } else if (ticket.status === 'pending_user') {
    statusPills.push({ icon: Clock, color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700', label: 'Pending', sub: 'User response needed' });
  } else if (ticket.status === 'new' || ticket.status === 'categorized') {
    statusPills.push({ icon: CircleDot, color: 'bg-slate-50 dark:bg-slate-900/20 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700', label: 'Submitted', sub: 'Awaiting assignment' });
  } else if (ticket.status === 'resolved') {
    statusPills.push({ icon: CheckCircle, color: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700', label: 'Resolved', sub: ticket.assigned_to_name || 'by IT Support' });
  } else if (ticket.status === 'closed') {
    statusPills.push({ icon: Lock, color: 'bg-slate-50 dark:bg-slate-900/20 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700', label: 'Closed', sub: ticket.resolved_at ? formatDateTime(ticket.resolved_at) : '' });
  }

  // Metadata line
  const meta = [
    ticket.priority && `Priority ${ticket.priority?.toUpperCase()}`,
    ticket.urgency_level && urgLabels[ticket.urgency_level],
    ticket.category_name,
    ticket.application_name,
    ticket.created_at && `Since ${formatDateTime(ticket.created_at)}`,
  ].filter(Boolean).join(' · ');

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5 mb-6" data-testid="ticket-status-banner">
      <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-stretch">

        {/* Column 1 — Type + Number */}
        <div className="flex flex-row lg:flex-col items-center lg:items-start gap-3 lg:gap-2 lg:min-w-[110px] lg:border-r dark:lg:border-slate-700 lg:pr-5">
          <span className={`${ts.badge} text-xs font-bold px-3 py-1.5 rounded-lg uppercase tracking-wide`}>
            {typeLabel}
          </span>
          <span className="font-mono text-lg font-bold text-slate-700 dark:text-slate-200 whitespace-nowrap">
            #{ticket.ticket_number}
          </span>
          {(ticket.priority || ticket.urgency_level) && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${prColors[ticket.priority] || 'bg-slate-200 text-slate-700'}`}>
              {ticket.priority ? ticket.priority.toUpperCase() : urgLabels[ticket.urgency_level]}
            </span>
          )}
        </div>

        {/* Column 2 — Title + Meta */}
        <div className="lg:w-52 lg:border-r dark:lg:border-slate-700 lg:pr-5 flex-shrink-0">
          <h2 className="font-bold text-slate-800 dark:text-white text-base leading-snug line-clamp-3 mb-2">
            {ticket.title}
          </h2>
          {meta && <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed">{meta}</p>}
          {ticket.reporter_name && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 flex items-center gap-1">
              <User className="w-3 h-3" /> {ticket.reporter_name}
            </p>
          )}
        </div>

        {/* Column 3 — Stage tracker (main) */}
        <div className="flex-1 min-w-0">
          {/* Stage icons + labels */}
          <div className="flex items-start justify-between mb-2">
            {stages.map((stage, idx) => {
              const Icon = stage.icon;
              const isDone    = idx < currentIdx;
              const isCurrent = idx === currentIdx;
              const isFuture  = idx > currentIdx;
              return (
                <div key={stage.key} className="flex flex-col items-center gap-1 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    isDone    ? 'bg-slate-600 dark:bg-slate-500 text-white' :
                    isCurrent ? 'bg-amber-500 text-white shadow-md shadow-amber-200 dark:shadow-amber-900/30' :
                    'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`text-center leading-tight text-[10px] font-medium hidden sm:block ${
                    isCurrent ? 'text-amber-600 dark:text-amber-400 font-bold' :
                    isDone    ? 'text-slate-500 dark:text-slate-400' :
                    'text-slate-300 dark:text-slate-600'
                  }`}>
                    {stage.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Segmented progress bar */}
          <div className="flex gap-0.5 mb-3 rounded-full overflow-hidden h-2.5">
            {stages.map((stage, idx) => (
              <div key={stage.key} className={`flex-1 transition-all duration-500 ${
                idx < currentIdx  ? 'bg-slate-500 dark:bg-slate-400' :
                idx === currentIdx ? 'bg-amber-500' :
                'bg-slate-100 dark:bg-slate-700'
              }`} />
            ))}
          </div>

          {/* Current status pills */}
          <div className="flex flex-wrap gap-2">
            {statusPills.map((pill, i) => {
              const PillIcon = pill.icon;
              return (
                <div key={i} className={`${pill.color} border rounded-xl px-3 py-2 flex items-start gap-2 text-xs min-w-[120px]`}>
                  <PillIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">{pill.label}</div>
                    {pill.sub && <div className="opacity-75 mt-0.5">{pill.sub}</div>}
                  </div>
                </div>
              );
            })}
            {statusPills.length === 0 && (
              <span className="text-xs text-slate-400 dark:text-slate-500 italic">No active assignment</span>
            )}
          </div>
        </div>

        {/* Column 4 — ETA */}
        {eta && (
          <div className="lg:border-l dark:lg:border-slate-700 lg:pl-5 flex-shrink-0 flex flex-row lg:flex-col items-center justify-center gap-1 lg:min-w-[90px]">
            <div className="bg-slate-100 dark:bg-slate-700 rounded-2xl px-5 py-3 text-center">
              <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1 justify-center">
                <Calendar className="w-3 h-3" /> ETA
              </div>
              <div className={`text-base font-bold ${eta.color}`}>{eta.text}</div>
              {ticket.resolution_due && (
                <div className="text-[10px] text-slate-400 mt-1">
                  {formatDateTime(ticket.resolution_due)}
                </div>
              )}
            </div>
            {['resolved', 'closed'].includes(ticket.status) && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl px-4 py-2 text-center mt-1">
                <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto mb-0.5" />
                <div className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                  {ticket.status === 'resolved' ? 'Resolved' : 'Closed'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_OPTIONS_FULL       = ['new', 'assigned', 'in_progress', 'pending_user', 'resolved', 'closed'];
const STATUS_OPTIONS_IT_SUPPORT = ['in_progress', 'pending_user', 'resolved', 'closed'];
const prClass = { p1: 'bg-rose-100 text-rose-800', p2: 'bg-amber-100 text-amber-800', p3: 'bg-blue-100 text-blue-800', p4: 'bg-slate-200 text-slate-700' };
const stClass = { new: 'bg-slate-100 text-slate-700', assigned: 'bg-blue-100 text-blue-800', in_progress: 'bg-emerald-100 text-emerald-800', pending_user: 'bg-amber-100 text-amber-800', resolved: 'bg-emerald-200 text-emerald-900', closed: 'bg-slate-200 text-slate-600', pending_approval: 'bg-purple-100 text-purple-800' };

export default function TicketDetailPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEndUser = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'end_user';
  const isItSupport = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'it_support';
  const isItManager = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'it_manager';
  const isAdmin = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'admin';
  // Ticket is "locked" if it was rejected by any approver OR has terminal status
  const isTicketLocked = (ticket) => {
    if (!ticket) return false;
    if (['cancelled', 'closed'].includes(ticket.status)) return true;
    if (ticket.approval_status === 'rejected') return true;
    if (approvals.some(a => a.status === 'rejected')) return true;
    return false;
  };
  const STATUS_OPTIONS = isItSupport ? STATUS_OPTIONS_IT_SUPPORT : STATUS_OPTIONS_FULL;
  // isOwnTicket: only relevant for end users — true if they raised the ticket
  // We check after ticket loads; default true so non-end-users are unaffected
  const [isOwnTicket, setIsOwnTicket] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [users, setUsers] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachPolicy, setAttachPolicy] = useState({ max_attachments: 3, max_attachment_size_mb: 10 });
  const [approvals, setApprovals] = useState([]);
  const [euAction, setEuAction] = useState(null);
  const [euComment, setEuComment] = useState('');
  const [euSubmitting, setEuSubmitting] = useState(false);
  const [showClosePanel, setShowClosePanel] = useState(false);
  const [resolutionCode, setResolutionCode] = useState('fixed');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [newWorkNote, setNewWorkNote] = useState('');
  const [addingWorkNote, setAddingWorkNote] = useState(false);
  const [resSummary, setResSummary] = useState('');
  const [editingResSummary, setEditingResSummary] = useState(false);
  const [savingResSummary, setSavingResSummary] = useState(false);
  const [sevForm, setSevForm] = useState({ impact: '', urgency: '', urgency_level: '', risk_level: '', cr_priority: '' });
  const [savingSev, setSavingSev] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);

  // ── Computed values — MUST come AFTER all useState declarations ───────────
  // Can update status/priority: admin, IT manager, the assigned IT Support, L1/L2/L3 leads, approvers
  const isAssignedToMe = ticket && user && (
    String(ticket.assigned_to) === String(user.id) ||
    String(ticket.assigned_to_id) === String(user.id)
  );
  const isTicketApprover = approvals.some(a => String(a.approver) === String(user?.id));
  const canUpdateTicket = isAdmin || isItManager || isAssignedToMe || isTicketApprover;
  // Computed once — true when ticket is cancelled/closed/rejected (read-only state)
  const ticketLocked = isTicketLocked(ticket);
  const isCancelled = ticket?.status === 'cancelled';

  const api = type === 'incident' ? incidentAPI : type === 'sr' ? serviceRequestAPI : changeRequestAPI;
  const typeLabel = type === 'incident' ? 'Incident' : type === 'sr' ? 'Service Request' : 'Change Request';
  const TypeIcon = type === 'incident' ? AlertCircle : type === 'sr' ? Settings : GitBranch;

  useEffect(() => {
    const load = async () => {
      try {
        const [res, uRes] = await Promise.all([api.get(id), userAPI.list()]);
        setTicket(res.data);
        setNewStatus(res.data.status);
        setAssignTo(res.data.assigned_to || '');
        // Initialize severity form
        setSevForm({
          impact: res.data.impact || '',
          urgency: res.data.urgency || '',
          urgency_level: res.data.urgency_level || '',
          risk_level: res.data.risk_level || '',
          cr_priority: res.data.cr_priority || '',
        });
        // Initialize resolution summary
        setResSummary(res.data.resolution_notes || '');
        // Determine ownership for end users (ticket.reporter is the UUID)
        if (isEndUser) {
          setIsOwnTicket(String(res.data.reporter) === String(user?.id));
        }
        // Fetch users relevant to this ticket's team
        const allUsersData = uRes.data?.results || uRes.data || [];
        // If ticket has assigned_group, show only that team's members
        if (res.data.assigned_group) {
          try {
            const teamRes = await import('../services/api').then(m => m.teamAPI.get(res.data.assigned_group));
            const teamMembers = (teamRes.data?.members || []).map(m => ({ id: m.user, full_name: m.user_name, email: m.user_email }));
            setUsers(teamMembers.length > 0 ? teamMembers : allUsersData);
          } catch { setUsers(allUsersData); }
        } else if (res.data.application) {
          // Find team for this application
          try {
            const teamsRes = await import('../services/api').then(m => m.teamAPI.list());
            const teams = teamsRes.data?.results || teamsRes.data || [];
            const appTeam = teams.find(t => t.application === res.data.application);
            if (appTeam) {
              const teamDetail = await import('../services/api').then(m => m.teamAPI.get(appTeam.id));
              const teamMembers = (teamDetail.data?.members || []).map(m => ({ id: m.user, full_name: m.user_name, email: m.user_email }));
              setUsers(teamMembers.length > 0 ? teamMembers : allUsersData);
            } else { setUsers(allUsersData); }
          } catch { setUsers(allUsersData); }
        } else {
          setUsers(allUsersData);
        }
        // Fetch comments for ALL ticket types
        try { const cRes = await api.getComments(id); setComments(cRes.data || []); } catch {}
        // Mark comments as read (triggers bell count update + read receipts)
        try { await api.markCommentsRead(id); } catch {}
        // Fetch attachments
        try {
          const { default: apiClient } = await import('../services/api');
          const aRes = await apiClient.get(`/tickets/attachments/?ticket_type=${type}&ticket_id=${id}`);
          setAttachments(aRes.data?.results || aRes.data || []);
        } catch {}
        // Fetch attachment policy from tenant settings
        try {
          const { api: apiInst } = await import('../services/api');
          const tRes = await apiInst.get('/tenants/settings/');
          setAttachPolicy({
            max_attachments: tRes.data?.max_attachments ?? 3,
            max_attachment_size_mb: tRes.data?.max_attachment_size_mb ?? 10,
          });
        } catch {}
        // Fetch audit history
        try { const hRes = await api.getHistory(id); setAuditHistory(hRes.data || []); } catch {}
        // Fetch approvals for SR/CR
        if (type === 'sr' || type === 'cr') {
          try {
            const { default: apiClient2 } = await import('../services/api');
            const appRes = await apiClient2.get(`/approvals/?ticket_type=${type}&ticket_id=${id}`);
            setApprovals(appRes.data?.results || appRes.data || []);
          } catch {}
        }
      } catch { toast.error('Failed to load ticket'); navigate(-1); }
      finally { setLoading(false); }
    };
    load(); // eslint-disable-next-line
  }, [id, type]);

  const handleStatusUpdate = async () => {
    if (newStatus === ticket.status) return;
    // 'closed' requires resolution details — show panel instead of saving
    if (newStatus === 'closed') { setShowClosePanel(true); return; }
    setUpdating(true);
    try {
      await api.updateStatus(id, newStatus);
      setTicket(prev => ({ ...prev, status: newStatus }));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setUpdating(false); }
  };

  const handleCloseWithResolution = async () => {
    if (!resolutionNotes.trim()) { toast.error('Resolution notes are required'); return; }
    setUpdating(true);
    try {
      await api.updateStatus(id, 'closed', { resolution_code: resolutionCode, resolution_notes: resolutionNotes });
      setTicket(prev => ({ ...prev, status: 'closed', resolution_code: resolutionCode, resolution_notes: resolutionNotes }));
      toast.success('Ticket closed with resolution notes');
      setShowClosePanel(false); setResolutionCode('fixed'); setResolutionNotes('');
    } catch { toast.error('Failed to close ticket'); }
    finally { setUpdating(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await api.addComment(id, { comment: newComment, is_internal: false });
      setComments(prev => [res.data, ...prev]);
      setNewComment('');
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleAssign = async () => {
    if (!assignTo) return;
    try {
      await api.assign(id, { assigned_to: assignTo });
      const assignedUser = users.find(u => u.id === assignTo);
      setTicket(prev => ({ ...prev, assigned_to: assignTo, assigned_to_name: assignedUser?.full_name || '', status: 'assigned' }));
      toast.success('Ticket assigned');
    } catch { toast.error('Failed to assign'); }
  };

  // End-user: close or reopen with mandatory comment
  const handleEuAction = async () => {
    if (!euComment.trim()) { toast.error('Please enter a reason'); return; }
    const newStatusValue = euAction === 'withdraw' ? 'cancelled' : 'reopened';
    setEuSubmitting(true);
    try {
      // Add comment FIRST (before status update) — backend blocks comments on cancelled tickets
      const commentPrefix = euAction === 'withdraw' ? '[Withdrawn by reporter]' : '[Reopened by reporter]';
      await api.addComment(id, {
        comment: `${commentPrefix} ${euComment}`,
        is_internal: false,
      });
      // Now update the status
      const extra = euAction === 'withdraw' ? { resolution_notes: euComment } : {};
      await api.updateStatus(id, newStatusValue, extra);
      setTicket(prev => ({ ...prev, status: newStatusValue }));
      setComments(prev => [{
        id: Date.now(),
        comment: `${commentPrefix} ${euComment}`,
        author_name: 'You', created_at: new Date().toISOString(), is_internal: false,
      }, ...prev]);
      toast.success(`Ticket ${euAction === 'withdraw' ? 'withdrawn' : 'reopened'} successfully`);
      setEuAction(null); setEuComment('');
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to update ticket');
    }
    finally { setEuSubmitting(false); }
  };

  const handleAddWorkNote = async () => {
    if (!newWorkNote.trim()) return;
    setAddingWorkNote(true);
    try {
      await api.addComment(id, { comment: newWorkNote, is_internal: true });
      const cRes = await api.getComments(id);
      setComments(cRes.data || []);
      setNewWorkNote('');
      toast.success('Work note added');
    } catch { toast.error('Failed to add work note'); }
    finally { setAddingWorkNote(false); }
  };

  const handleSaveResSummary = async () => {
    setSavingResSummary(true);
    try {
      await api.patch(id, { resolution_notes: resSummary });
      setTicket(prev => ({ ...prev, resolution_notes: resSummary }));
      setEditingResSummary(false);
      toast.success('Resolution summary saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingResSummary(false); }
  };

  const handleChangeSeverity = async () => {
    const data = {};
    if (type === 'incident') {
      if (sevForm.impact) data.impact = sevForm.impact;
      if (sevForm.urgency) data.urgency = sevForm.urgency;
    } else if (type === 'sr') {
      if (sevForm.urgency_level) data.urgency_level = sevForm.urgency_level;
    } else if (type === 'cr') {
      if (sevForm.risk_level) data.risk_level = sevForm.risk_level;
      if (sevForm.cr_priority) data.cr_priority = sevForm.cr_priority;
    }
    if (!Object.keys(data).length) return;
    setSavingSev(true);
    try {
      // Use updatePriority endpoint which recalculates SLA automatically
      const res = await api.updatePriority(id, data);
      setTicket(res.data);
      // Reset SLA form display values
      setSevForm({
        impact: res.data.impact || '',
        urgency: res.data.urgency || '',
        urgency_level: res.data.urgency_level || '',
        risk_level: res.data.risk_level || '',
        cr_priority: res.data.cr_priority || '',
      });
      toast.success(`Priority updated — SLA recalculated (${res.data.sla_paused ? 'paused outside BH' : 'timer running'})`);
    } catch { toast.error('Failed to update priority'); }
    finally { setSavingSev(false); }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const file of files) {
      try {
        await uploadAttachment(type, id, file);
        toast.success(`${file.name} uploaded`);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    // Refresh attachment list
    try {
      const { default: apiClient } = await import('../services/api');
      const aRes = await apiClient.get(`/tickets/attachments/?ticket_type=${type}&ticket_id=${id}`);
      setAttachments(aRes.data?.results || aRes.data || []);
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  if (!ticket) return null;

  return (
    <div data-testid="ticket-detail-page">
      {/* Read-only notice for end users viewing others' tickets */}
      {isEndUser && !isOwnTicket && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span><strong>Read-only view.</strong> You can only add comments, attachments, or close/reopen tickets that you raised.</span>
        </div>
      )}
      {/* Rejection banner — shown to all non-end-users when ticket is rejected */}
      {!isEndUser && ticket && (approvals.some(a => a.status === 'rejected') || ticket.approval_status === 'rejected') && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-sm text-rose-800 dark:text-rose-300">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          <span><strong>Approval Rejected.</strong> This ticket was rejected by an approver. Status updates and assignment are locked. The ticket is now <strong>cancelled</strong>.</span>
        </div>
      )}
      {/* Cancelled — Read-Only banner */}
      {isCancelled && (
        <div className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Lock className="w-4 h-4 flex-shrink-0 text-slate-500" />
          <span>
            <strong>Ticket Cancelled — Read Only.</strong> SLA has been stopped. No further comments, work notes, or attachments can be added.
          </span>
        </div>
      )}

      {/* Back Button + Title */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <TypeIcon className="w-5 h-5 text-sky-600" />
            <span className="font-mono text-lg font-bold text-sky-700">{ticket.ticket_number}</span>
            <span className={`${stClass[ticket.status] || 'bg-slate-100'} px-2.5 py-1 rounded-full text-xs font-medium`}>
              {ticket.status?.replace(/_/g, ' ')}
            </span>
            {ticket.priority && <span className={`${prClass[ticket.priority] || 'bg-slate-100'} px-2.5 py-1 rounded-full text-xs font-semibold`}>{ticket.priority?.toUpperCase()}</span>}
          </div>
          <h1 className="text-xl font-bold text-slate-800 mt-1">{ticket.title}</h1>
        </div>
      </div>

      {/* Status Banner — stage tracker, SLA countdown, status pills */}
      <TicketStatusBanner ticket={ticket} type={type} typeLabel={typeLabel} approvals={approvals} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-semibold text-slate-800 mb-3">Description</h3>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Approval Timeline */}
          {(type === 'sr' || type === 'cr') && approvals.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-6">
              <h3 className="font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-sky-500" /> Approval Timeline
              </h3>
              <div className="space-y-3">
                {approvals.sort((a, b) => a.level - b.level).map(a => (
                  <div key={a.id} className={`flex items-start gap-3 p-3 rounded-lg ${a.status === 'approved' ? 'bg-emerald-50 dark:bg-emerald-900/20' : a.status === 'rejected' ? 'bg-rose-50 dark:bg-rose-900/20' : a.status === 'skipped' ? 'bg-slate-50 dark:bg-slate-900' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
                    <div className="mt-0.5">
                      {a.status === 'approved' && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                      {a.status === 'rejected' && <XCircle className="w-5 h-5 text-rose-500" />}
                      {a.status === 'pending' && <CircleDot className="w-5 h-5 text-amber-500 animate-pulse" />}
                      {a.status === 'skipped' && <XCircle className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm dark:text-white">Level {a.level}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          a.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                          a.status === 'rejected' ? 'bg-rose-100 text-rose-800' :
                          a.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                          'bg-slate-100 text-slate-600'
                        }`}>{a.status}</span>
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                        Approver: <span className="font-medium dark:text-slate-300">{a.approver_name}</span>
                        {a.actioned_at && <span className="ml-2">· {formatDateTime(a.actioned_at)}</span>}
                      </div>
                      {a.comments && <p className="text-sm text-slate-700 dark:text-slate-300 mt-1 italic">"{a.comments}"</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Work Notes — Internal, IT Staff only — NOT visible to end users */}
          {!isEndUser && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-amber-800 p-6" data-testid="work-notes-section">
              <h3 className="font-semibold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                Work Notes <span className="text-xs font-normal text-amber-600 dark:text-amber-400 ml-1">(Internal — not visible to end users)</span>
                <span className="ml-auto text-xs text-slate-400">({comments.filter(c => c.is_internal).length})</span>
              </h3>
              {isCancelled ? (
                <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 rounded-xl px-4 py-2.5 mb-4 text-sm text-slate-400 border border-slate-200 dark:border-slate-700">
                  <Lock className="w-4 h-4" /> Ticket is cancelled — work notes are locked
                </div>
              ) : (
              <div className="flex gap-2 mb-4">
                <textarea
                  value={newWorkNote} onChange={e => setNewWorkNote(e.target.value)}
                  placeholder="Add a work note — document what you investigated, current status, steps taken, pending actions..."
                  rows={3}
                  className="flex-1 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-2.5 text-sm bg-amber-50 dark:bg-amber-900/10 dark:text-white resize-none"
                  data-testid="work-note-input"
                />
                <button onClick={handleAddWorkNote} disabled={addingWorkNote || !newWorkNote.trim()}
                  className="bg-amber-500 text-white px-4 py-2.5 rounded-xl text-sm hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1.5 self-end"
                  data-testid="add-work-note-btn">
                  {addingWorkNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              )}
              {comments.filter(c => c.is_internal).length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">No work notes yet — document your troubleshooting progress here</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {comments.filter(c => c.is_internal).map(c => (
                    <div key={c.id} className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 border border-amber-100 dark:border-amber-800">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-3 h-3 text-amber-600" />
                        <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">{c.author_name}</span>
                        <span className="text-xs text-slate-400">{c.created_at ? formatDateTime(c.created_at) : ''}</span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{c.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Comments — Public (visible to all) */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-6">
              <h3 className="font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-sky-500" /> Comments
                <span className="text-xs font-normal text-slate-400 ml-1">(visible to all)</span>
                <span className="ml-auto text-xs text-slate-400">({comments.filter(c => !c.is_internal).length})</span>
              </h3>
              {/* Add comment — only for non-end-users or end user's own ticket */}
              {(!isEndUser || isOwnTicket) ? (
                isCancelled ? (
                  <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 rounded-xl px-4 py-2.5 mb-4 text-sm text-slate-400 border border-slate-200 dark:border-slate-700">
                    <Lock className="w-4 h-4" /> Ticket is cancelled — comments are locked
                  </div>
                ) : (
                <div className="flex gap-2 mb-4">
                  <input
                    value={newComment} onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                    placeholder="Add a comment..."
                    className="flex-1 border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm dark:bg-slate-900 dark:text-white"
                    data-testid="comment-input"
                  />
                  <button onClick={handleAddComment} className="bg-sky-500 text-white px-4 py-2.5 rounded-xl text-sm hover:bg-sky-600 flex items-center gap-1.5" data-testid="add-comment-btn">
                    <Send className="w-4 h-4" /> Send
                  </button>
                </div>
                )
              ) : (
                <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 rounded-xl px-4 py-2.5 mb-4 text-sm text-slate-400">
                  <MessageSquare className="w-4 h-4" /> Read-only — you can only comment on your own tickets
                </div>
              )}
              {comments.filter(c => !c.is_internal).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No comments yet</p>
              ) : (
                <div className="space-y-3">
                  {comments.filter(c => !c.is_internal).map(c => {
                    const isMyComment = String(c.author) === String(user?.id);
                    const readBy = c.read_by || [];
                    const readByOther = isMyComment && readBy.some(uid => uid !== String(user?.id));
                    return (
                      <div key={c.id} className={`rounded-lg p-3 ${isMyComment ? 'bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/50 ml-8' : 'bg-slate-50 dark:bg-slate-900 mr-8'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-3.5 h-3.5 text-slate-500" />
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{c.author_name}</span>
                          <span className="text-xs text-slate-400">{c.created_at ? formatDateTime(c.created_at) : ''}</span>
                          {/* Read receipt ticks — only shown to the comment author */}
                          {isMyComment && (
                            <span className={`ml-auto flex items-center gap-0.5 text-xs ${readByOther ? 'text-sky-500' : 'text-slate-400'}`} title={readByOther ? 'Read' : 'Delivered'}>
                              {readByOther ? (
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <polyline points="2 12 7 17 14 7" /><polyline points="9 12 14 17 21 7" />
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <polyline points="5 12 10 17 19 7" />
                                </svg>
                              )}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-200">{c.comment}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          {/* Resolution Summary — IT staff can write, all can read */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-6" data-testid="resolution-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" /> Resolution Summary
              </h3>
              {!isEndUser && !editingResSummary && (
                <button onClick={() => setEditingResSummary(true)}
                  className="text-xs text-sky-600 dark:text-sky-400 hover:underline flex items-center gap-1">
                  <Edit className="w-3 h-3" /> {resSummary ? 'Edit' : 'Add Summary'}
                </button>
              )}
            </div>
            {editingResSummary ? (
              <div className="space-y-2">
                <textarea rows={4} value={resSummary} onChange={e => setResSummary(e.target.value)}
                  placeholder="Describe the root cause, steps taken to resolve, and any follow-up actions required..."
                  className="w-full border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white resize-none"
                  data-testid="resolution-summary-input" autoFocus />
                <div className="flex gap-2">
                  <button onClick={handleSaveResSummary} disabled={savingResSummary}
                    className="bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-1.5">
                    {savingResSummary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save
                  </button>
                  <button onClick={() => { setEditingResSummary(false); setResSummary(ticket.resolution_notes || ''); }}
                    className="px-4 py-2 rounded-xl text-sm bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    Cancel
                  </button>
                </div>
              </div>
            ) : resSummary ? (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-xl p-4">
                <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{resSummary}</p>
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic py-2">
                {isEndUser ? 'No resolution summary yet.' : 'No resolution summary added yet. Click "Add Summary" to document how this ticket was resolved.'}
              </p>
            )}
          </div>

          {/* ── Audit Trail — inside main content, below resolution summary ── */}
          {!isEndUser && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-6" data-testid="audit-trail-section">
              <h3 className="font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-slate-500" /> Audit Trail
                <span className="ml-auto text-xs text-slate-400">({auditHistory.length} entries)</span>
              </h3>
              {auditHistory.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No history yet</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {auditHistory.map(h => {
                    const isAttachment = h.field_changed === 'attachment_uploaded';
                    return (
                    <div key={h.id} className="flex gap-3 text-xs py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${isAttachment ? 'bg-emerald-400' : 'bg-sky-400'}`} />
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-slate-700 dark:text-slate-300">{h.changed_by_name || 'System'}</span>
                        <span className="text-slate-500 dark:text-slate-400 mx-1">—</span>
                        {isAttachment ? (
                          <span className="text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                            <Paperclip className="w-3 h-3" /> uploaded attachment
                            <span className="text-slate-500 dark:text-slate-400 ml-1 font-mono">{h.new_value}</span>
                          </span>
                        ) : (
                          <>
                            <span className="text-slate-600 dark:text-slate-300 capitalize">{h.field_changed?.replace(/_/g, ' ')}</span>
                            {h.old_value && h.new_value && h.old_value !== h.new_value && (
                              <span className="text-slate-500 dark:text-slate-400 ml-1">
                                <span className="line-through text-rose-400">{h.old_value.replace(/_/g,' ')}</span>
                                <span className="mx-1">→</span>
                                <span className="text-emerald-500">{h.new_value.replace(/_/g,' ')}</span>
                              </span>
                            )}
                            {(!h.old_value || h.field_changed === 'created') && (
                              <span className="text-slate-500 dark:text-slate-400 ml-1">{h.new_value}</span>
                            )}
                          </>
                        )}
                      </div>
                      <div className="text-slate-400 flex-shrink-0 whitespace-nowrap">{h.changed_at ? formatDateTime(h.changed_at) : ''}</div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar Details */}
        <div className="space-y-4">
          {/* ── Details (first — most important) ── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5">
            <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Type</span><span className="font-medium dark:text-white">{typeLabel}</span></div>
              {ticket.application_name && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Application / System</span><span className="font-medium dark:text-white text-right">{ticket.application_name}</span></div>}
              {ticket.category_name && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Category</span><span className="font-medium dark:text-white text-right">{ticket.category_name}</span></div>}
              {ticket.priority && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Priority</span><span className={`${prClass[ticket.priority] || ''} px-2 py-0.5 rounded text-xs font-semibold`}>{ticket.priority?.toUpperCase()}</span></div>}
              {ticket.impact && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Impact</span><span className="font-medium dark:text-white capitalize">{ticket.impact}</span></div>}
              {ticket.urgency && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Urgency</span><span className="font-medium dark:text-white capitalize">{ticket.urgency}</span></div>}
              {ticket.urgency_level && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">SR Urgency</span><span className="font-medium dark:text-white capitalize">{ticket.urgency_level?.replace(/_/g, ' ')}</span></div>}
              {ticket.change_type && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Change Type</span><span className="font-medium dark:text-white capitalize">{ticket.change_type}</span></div>}
              {ticket.risk_level && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Risk Level</span><span className="font-medium dark:text-white capitalize">{ticket.risk_level}</span></div>}
              {ticket.approval_status && ticket.approval_status !== 'none' && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Approval</span><span className={`font-medium capitalize ${ticket.approval_status === 'approved' ? 'text-emerald-600' : ticket.approval_status === 'rejected' ? 'text-rose-600' : 'text-amber-600'}`}>{ticket.approval_status}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Reporter</span><span className="font-medium dark:text-white">{ticket.reporter_name || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Assignee</span><span className="font-medium dark:text-white">{ticket.assigned_to_name || 'Unassigned'}</span></div>
              {ticket.assigned_group_name && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Team</span><span className="font-medium dark:text-white">{ticket.assigned_group_name}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Created</span><span className="font-medium dark:text-white text-xs">{ticket.created_at ? formatDateTime(ticket.created_at) : ''}</span></div>
              {ticket.resolved_at && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Resolved</span><span className="font-medium dark:text-white text-xs">{formatDateTime(ticket.resolved_at)}</span></div>}
            </div>
          </div>

          {/* ── SLA (second — critical timing info) ── */}
          {(ticket.response_due || ticket.resolution_due) && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5">
              <h3 className="font-semibold text-slate-800 dark:text-white mb-3 flex items-center gap-2"><Timer className="w-4 h-4 text-sky-500" /> SLA</h3>
              <div className="space-y-2 text-sm">
                {ticket.created_at && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Created</span><span className="font-medium text-slate-700 dark:text-slate-300 text-xs">{formatDateTime(ticket.created_at)}</span></div>}
                {ticket.response_due && (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Response Due</span><span className={`font-medium text-xs ${ticket.sla_response_met === false ? 'text-rose-600' : 'text-slate-700 dark:text-slate-300'}`}>{formatDateTime(ticket.response_due)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Time to Respond</span><span className={`font-medium text-xs px-2 py-0.5 rounded-full ${ticket.first_response_at ? (ticket.sla_response_met === false ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700') : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>{ticket.first_response_at ? (() => { const ms = new Date(ticket.first_response_at) - new Date(ticket.created_at); const h = Math.floor(ms/3600000); const m = Math.floor((ms%3600000)/60000); return h > 0 ? `${h}h ${m}m` : `${m}m`; })() : 'Not responded yet'}</span></div>
                  </>
                )}
                {ticket.resolution_due && (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Resolution Due</span><span className={`font-medium text-xs ${ticket.sla_resolution_met === false ? 'text-rose-600' : 'text-slate-700 dark:text-slate-300'}`}>{formatDateTime(ticket.resolution_due)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">{ticket.resolved_at ? 'Time to Resolve' : 'Elapsed'}</span><span className={`font-medium text-xs px-2 py-0.5 rounded-full ${ticket.resolved_at ? (ticket.sla_resolution_met === false ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700') : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400'}`}>{(() => { const end = ticket.resolved_at ? new Date(ticket.resolved_at) : new Date(); const ms = end - new Date(ticket.created_at); const d = Math.floor(ms/86400000); const h = Math.floor((ms%86400000)/3600000); const m = Math.floor((ms%3600000)/60000); return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`; })()} {!ticket.resolved_at && 'elapsed'}</span></div>
                  </>
                )}
                {ticket.sla_paused && <div className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-lg border border-amber-200 dark:border-amber-800">SLA Timer is PAUSED</div>}
                <div className="pt-1 border-t dark:border-slate-700 text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> All times in tenant timezone</div>
              </div>
              {/* Pause/Resume SLA — Incidents, IT Manager+ only */}
              {type === 'incident' && !isEndUser && (
                <div className="flex gap-2 mt-3">
                  {!ticket.sla_paused ? (
                    <button onClick={async () => { try { await incidentAPI.pauseSla(id); setTicket(p => ({...p, sla_paused: true})); toast.success('SLA paused'); } catch { toast.error('Failed'); } }}
                      className="flex-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-3 py-2 rounded-lg text-xs hover:bg-amber-100 flex items-center justify-center gap-1.5" data-testid="pause-sla">
                      <PauseCircle className="w-4 h-4" /> Pause SLA
                    </button>
                  ) : (
                    <button onClick={async () => { try { await incidentAPI.resumeSla(id); setTicket(p => ({...p, sla_paused: false})); toast.success('SLA resumed'); } catch { toast.error('Failed'); } }}
                      className="flex-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-3 py-2 rounded-lg text-xs hover:bg-emerald-100 flex items-center justify-center gap-1.5" data-testid="resume-sla">
                      <PlayCircle className="w-4 h-4" /> Resume SLA
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Status Update — only assigned IT Support, L1/L2/L3, Admin, IT Manager */}
          {!isEndUser && (canUpdateTicket || !isItSupport) && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5">
            <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Update Status</h3>
            {(() => {
              // SR/CR: lock only while L1 has NOT yet approved
              // Once L1 approves, unlock even if L2/L3 are still pending
              const l1Approved = approvals.some(a => Number(a.level) === 1 && a.status === 'approved');
              const approvalPending = (type === 'sr' || type === 'cr') && ticket.approval_status === 'pending' && approvals.length > 0 && !l1Approved;
              if (approvalPending) {
                return (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2.5 text-xs text-amber-800 dark:text-amber-400">
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                      <span>Status updates are locked until L1 approval is complete.</span>
                    </div>
                    <div className="flex gap-2 opacity-40 pointer-events-none">
                      <select className="flex-1 border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white cursor-not-allowed" disabled>
                        <option>— Awaiting Approval —</option>
                      </select>
                      <button disabled className="px-3 py-2 rounded-xl text-sm bg-slate-300 text-white font-medium cursor-not-allowed">Update</button>
                    </div>
                  </div>
                );
              }
              return isTicketLocked(ticket) ? (
              <div className="text-xs text-slate-400 bg-slate-50 dark:bg-slate-900 rounded-xl px-3 py-2.5">
                {approvals.some(a => a.status === 'rejected') || ticket.approval_status === 'rejected'
                  ? 'Ticket rejected by approver — no further status updates allowed.'
                  : `Ticket is ${ticket.status} — no further updates.`}
              </div>
            ) : !showClosePanel ? (
              <div className="flex gap-2">
                <select value={newStatus} onChange={e => { setNewStatus(e.target.value); setShowClosePanel(false); }}
                  className="flex-1 border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" data-testid="status-select">
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
                <button onClick={handleStatusUpdate} disabled={updating || newStatus === ticket.status}
                  className={`px-3 py-2 rounded-xl text-sm disabled:bg-slate-300 font-medium ${newStatus === 'closed' ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-sky-500 hover:bg-sky-600 text-white'}`}
                  data-testid="update-status-btn">
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : newStatus === 'closed' ? 'Close...' : 'Update'}
                </button>
              </div>
            ) : (
              /* Close with Resolution Panel */
              <div className="space-y-3" data-testid="close-resolution-panel">
                <div className="flex items-center gap-2 text-sm font-semibold text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 rounded-lg px-3 py-2">
                  <Lock className="w-4 h-4" /> Closing Ticket — Resolution Required
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">Resolution Code</label>
                  <select value={resolutionCode} onChange={e => setResolutionCode(e.target.value)}
                    className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" data-testid="resolution-code">
                    <option value="fixed">Fixed — Permanently resolved</option>
                    <option value="workaround">Workaround — Temporary fix applied</option>
                    <option value="not_reproducible">Not Reproducible — Could not reproduce</option>
                    <option value="duplicate">Duplicate — Already tracked elsewhere</option>
                    <option value="user_error">User Error — Caused by user action</option>
                    <option value="no_action_required">No Action Required</option>
                    <option value="third_party">Third Party — Vendor/external issue</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">
                    Resolution Notes <span className="text-rose-500">*</span>
                  </label>
                  <textarea rows={3} value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)}
                    placeholder="Describe how the issue was resolved, what was done, and any follow-up actions..."
                    className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white resize-none"
                    data-testid="resolution-notes" autoFocus />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCloseWithResolution} disabled={updating || !resolutionNotes.trim()}
                    className="flex-1 bg-rose-500 hover:bg-rose-600 text-white px-3 py-2 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid="confirm-close-btn">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-3.5 h-3.5" /> Confirm Close</>}
                  </button>
                  <button onClick={() => { setShowClosePanel(false); setNewStatus(ticket.status); }}
                    className="px-3 py-2 rounded-xl text-sm bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    Cancel
                  </button>
                </div>
              </div>
            );
            })()}
          </div>
          )}

          {/* Assign To — only Admin, L1/L2/L3 App leads, and ticket approvers can reassign */}
          {!isEndUser && !isItSupport && !isItManager && !isTicketLocked(ticket) && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5">
            <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Assign To</h3>
            {(() => {
              // SR/CR: lock only while L1 has NOT yet approved
              const l1Approved = approvals.some(a => Number(a.level) === 1 && a.status === 'approved');
              const approvalPending = (type === 'sr' || type === 'cr') && ticket.approval_status === 'pending' && approvals.length > 0 && !l1Approved;
              return (
                <>
                  {approvalPending && (
                    <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2.5 mb-3 text-xs text-amber-800 dark:text-amber-400">
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                      <span>Assignment is locked until L1 approval is complete. Once approved, you can assign or reassign the ticket.</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <select
                      value={assignTo}
                      onChange={e => setAssignTo(e.target.value)}
                      disabled={approvalPending}
                      className={`flex-1 border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white ${approvalPending ? 'opacity-50 cursor-not-allowed bg-slate-50' : ''}`}
                      data-testid="assign-select"
                    >
                      <option value="">— Select IT Support —</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                    </select>
                    <button
                      onClick={handleAssign}
                      disabled={!assignTo || approvalPending}
                      title={approvalPending ? 'Approval pending — assignment locked' : 'Assign ticket'}
                      className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        approvalPending
                          ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                          : 'bg-sky-500 text-white hover:bg-sky-600 disabled:bg-slate-300'
                      }`}
                      data-testid="assign-btn"
                    >
                      {ticket.assigned_to ? 'Reassign' : 'Assign'}
                    </button>
                  </div>
                </>
              );
            })()}
            </div>
          )}

          {/* Change Severity/Priority — only assigned IT Support, L1/L2/L3, Admin, IT Manager */}
          {!isEndUser && (canUpdateTicket || !isItSupport) && !isTicketLocked(ticket) && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5" data-testid="severity-panel">
            <h3 className="font-semibold text-slate-800 dark:text-white mb-3">
              {type === 'incident' ? 'Change Priority / Severity' : type === 'sr' ? 'Change Urgency' : 'Change Risk / Priority'}
            </h3>
            <div className="space-y-3">
              {type === 'incident' && (
                <>
                  <div>
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Impact</label>
                    <select value={sevForm.impact} onChange={e => setSevForm(p => ({...p, impact: e.target.value}))}
                      className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
                      <option value="">No change</option>
                      <option value="extensive">Extensive — Whole organisation affected</option>
                      <option value="significant">Significant — Multiple departments</option>
                      <option value="moderate">Moderate — Single department</option>
                      <option value="minor">Minor — Single user</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Urgency</label>
                    <select value={sevForm.urgency} onChange={e => setSevForm(p => ({...p, urgency: e.target.value}))}
                      className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
                      <option value="">No change</option>
                      <option value="critical">Critical — Business stopping</option>
                      <option value="high">High — Significant impact</option>
                      <option value="medium">Medium — Moderate impact</option>
                      <option value="low">Low — Can wait</option>
                    </select>
                  </div>
                  {(sevForm.impact || sevForm.urgency) && (
                    <div className="text-xs bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2 text-slate-500 dark:text-slate-400">
                      Current: <strong>P{ticket.priority?.toUpperCase()}</strong> · {ticket.impact} × {ticket.urgency}
                    </div>
                  )}
                </>
              )}
              {type === 'sr' && (
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Urgency Level</label>
                  <select value={sevForm.urgency_level} onChange={e => setSevForm(p => ({...p, urgency_level: e.target.value}))}
                    className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
                    <option value="">No change</option>
                    <option value="asap">ASAP — Same day</option>
                    <option value="this_week">This Week — Within 5 days</option>
                    <option value="standard">Standard — Within SLA window</option>
                    <option value="no_rush">No Rush — Low priority</option>
                  </select>
                </div>
              )}
              {type === 'cr' && (
                <>
                  <div>
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Risk Level</label>
                    <select value={sevForm.risk_level} onChange={e => setSevForm(p => ({...p, risk_level: e.target.value}))}
                      className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
                      <option value="">No change</option>
                      <option value="critical">Critical</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">CR Priority</label>
                    <select value={sevForm.cr_priority} onChange={e => setSevForm(p => ({...p, cr_priority: e.target.value}))}
                      className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white">
                      <option value="">No change</option>
                      <option value="immediate">Immediate</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </>
              )}
              <button onClick={handleChangeSeverity} disabled={savingSev}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-xl text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid="change-severity-btn">
                {savingSev ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Update Severity
              </button>
            </div>
          </div>
          )}

          {/* End User — Withdraw Request (own ticket, new/pending_approval only) + Reopen */}
          {isEndUser && isOwnTicket && type !== 'cr' && (() => {
            const st = ticket.status;
            // Withdraw: only available before IT work begins
            const canWithdraw = ['new', 'pending_approval'].includes(st);
            // Reopen: only once after resolved/closed
            const alreadyReopened = comments.some(c => c.comment?.startsWith('[Reopened by reporter]'));
            const canReopen = ['closed', 'resolved'].includes(st) && !alreadyReopened;

            if (!canWithdraw && !canReopen) {
              const isClosedResolved = ['closed', 'resolved'].includes(st);
              if (isClosedResolved && alreadyReopened) {
                return (
                  <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5">
                    <h3 className="font-semibold text-slate-800 dark:text-white mb-2">Ticket Actions</h3>
                    <div className="text-xs text-slate-400 bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2.5">
                      This ticket has already been reopened once. Please contact IT Support if the issue persists.
                    </div>
                  </div>
                );
              }
              return null;
            }
            return (
              <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5" data-testid="eu-ticket-actions">
                <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Ticket Actions</h3>
                {euAction === null && (
                  <div className="flex flex-col gap-2">
                    {canWithdraw && (
                      <button onClick={() => { setEuAction('withdraw'); setEuComment(''); }}
                        className="w-full flex items-center justify-center gap-2 bg-slate-50 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-100 transition-all border border-slate-200 dark:border-slate-700"
                        data-testid="eu-withdraw-btn">
                        <XOctagon className="w-4 h-4 text-slate-500" /> Withdraw Request
                      </button>
                    )}
                    {canReopen && (
                      <button onClick={() => { setEuAction('reopen'); setEuComment(''); }}
                        className="w-full flex items-center justify-center gap-2 bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-400 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-sky-100 transition-all border border-sky-200 dark:border-sky-800"
                        data-testid="eu-reopen-btn">
                        <RefreshCw className="w-4 h-4" /> Reopen Ticket
                      </button>
                    )}
                  </div>
                )}
                {euAction !== null && (
                  <div className="space-y-3" data-testid="eu-comment-panel">
                    <div className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg ${euAction === 'withdraw' ? 'bg-slate-50 text-slate-700 dark:bg-slate-900 dark:text-slate-300' : 'bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-400'}`}>
                      {euAction === 'withdraw' ? <XOctagon className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
                      {euAction === 'withdraw' ? 'Withdrawing this request' : 'Reopening this ticket'}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400 block mb-1">
                        Reason <span className="text-rose-500">*</span>
                      </label>
                      <textarea rows={3} value={euComment} onChange={e => setEuComment(e.target.value)}
                        placeholder={euAction === 'withdraw' ? 'Why are you withdrawing this request? (e.g. no longer needed, purchased separately)' : 'Why are you reopening? (e.g. issue recurred, solution not working)'}
                        className="w-full border dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-900 dark:text-white resize-none"
                        data-testid="eu-comment-input" autoFocus />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleEuAction} disabled={euSubmitting || !euComment.trim()}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50 ${euAction === 'withdraw' ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-sky-500 hover:bg-sky-600 text-white'}`}
                        data-testid="eu-confirm-btn">
                        {euSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (euAction === 'withdraw' ? 'Confirm Withdraw' : 'Confirm Reopen')}
                      </button>
                      <button onClick={() => { setEuAction(null); setEuComment(''); }}
                        className="px-4 py-2 rounded-xl text-sm bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Attachments */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-5">
            {/* Header with live limits */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-slate-500" />
                Attachments ({attachments.length}/{attachPolicy.max_attachments})
              </h3>
              <span className="text-xs text-slate-400">
                Max {attachPolicy.max_attachments} files · {attachPolicy.max_attachment_size_mb} MB each
              </span>
            </div>
            {/* Slot progress bar */}
            <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-1 mb-3">
              <div
                className={`h-1 rounded-full transition-all ${attachments.length >= attachPolicy.max_attachments ? 'bg-rose-400' : 'bg-sky-400'}`}
                style={{ width: `${Math.min(100, (attachments.length / attachPolicy.max_attachments) * 100)}%` }}
              />
            </div>
            {attachments.length > 0 && (
              <div className="space-y-2 mb-3">
                {attachments.map(a => {
                  const sizeKB = a.file_size ? (a.file_size / 1024) : 0;
                  const sizeLabel = sizeKB >= 1024
                    ? `${(sizeKB / 1024).toFixed(1)} MB`
                    : `${sizeKB.toFixed(1)} KB`;
                  // Can delete: uploader themselves, OR admin/IT Manager, but NOT on cancelled tickets
                  const canDelete = !isCancelled && (
                    isAdmin || isItManager ||
                    (a.uploaded_by && a.uploaded_by === user?.id)
                  );
                  return (
                  <div key={a.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2 text-sm group">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <a href={a.file} target="_blank" rel="noopener noreferrer"
                        className="text-sky-600 hover:text-sky-800 font-medium truncate">{a.file_name}</a>
                      {a.file_size > 0 && (
                        <span className="text-xs text-slate-400 flex-shrink-0">{sizeLabel}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs text-slate-400">{a.uploaded_by_name}</span>
                      {canDelete && (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete "${a.file_name}"? This cannot be undone.`)) return;
                            try {
                              const { default: apiClient } = await import('../services/api');
                              await apiClient.delete(`/tickets/attachments/${a.id}/`);
                              setAttachments(prev => prev.filter(x => x.id !== a.id));
                              toast.success('Attachment deleted');
                            } catch { toast.error('Failed to delete attachment'); }
                          }}
                          title="Delete attachment"
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-rose-100 dark:hover:bg-rose-900/30 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400"
                          data-testid={`delete-attachment-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            {/* Upload input with remaining slots info */}
            {(!isEndUser || isOwnTicket) && !isCancelled && (
              <div>
                {attachments.length >= attachPolicy.max_attachments ? (
                  <p className="text-xs text-rose-500 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                    Maximum {attachPolicy.max_attachments} attachments reached — remove one to add more
                  </p>
                ) : (
                  <div>
                    <input type="file" multiple
                      accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.tiff"
                      onChange={handleFileUpload}
                      className="text-sm file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100 dark:text-slate-300"
                      data-testid="upload-file" />
                    <p className="text-xs text-slate-400 mt-1">
                      {attachPolicy.max_attachments - attachments.length} slot{attachPolicy.max_attachments - attachments.length !== 1 ? 's' : ''} remaining · max {attachPolicy.max_attachment_size_mb} MB per file
                    </p>
                  </div>
                )}
              </div>
            )}
            {isEndUser && !isOwnTicket && attachments.length === 0 && (
              <p className="text-xs text-slate-400">No attachments</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
